import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";

export interface PhotoItem {
  id: string;
  caption: string | null;
  sortOrder: number;
  url: string;
}

export interface AttachmentItem {
  id: string;
  filename: string;
  sortOrder: number;
  url: string;
}

async function handleFileResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
    throw new ApiError(401, "AUTH_REQUIRED", "Not authenticated");
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText, code: "UNKNOWN" }));
    throw new ApiError(response.status, body.code ?? "UNKNOWN", body.error ?? "Request failed", body.details);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function uploadFile<T>(url: string, file: File, caption?: string): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  if (caption) formData.append("caption", caption);

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  return handleFileResponse<T>(res);
}

async function patchFile<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleFileResponse<T>(res);
}

// Photos

export function usePhotos(recordId: string | undefined) {
  return useQuery({
    queryKey: ["insurance", recordId, "photos"],
    queryFn: () => api.get<{ photos: PhotoItem[] }>(`/api/insurance/${recordId}/photos`),
    enabled: !!recordId,
  });
}

export function useUploadPhoto(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, caption }: { file: File; caption?: string }) =>
      uploadFile<{ photo: PhotoItem }>(`/api/insurance/${recordId}/photos`, file, caption),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "photos"] });
    },
  });
}

export function useUpdatePhoto(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ photoId, ...body }: { photoId: string; caption?: string; sortOrder?: number }) =>
      patchFile<{ photo: PhotoItem }>(`/api/insurance/${recordId}/photos/${photoId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "photos"] });
    },
  });
}

export function useDeletePhoto(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) => api.delete(`/api/insurance/${recordId}/photos/${photoId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "photos"] });
    },
  });
}

// Attachments

export function useAttachments(recordId: string | undefined) {
  return useQuery({
    queryKey: ["insurance", recordId, "attachments"],
    queryFn: () => api.get<{ attachments: AttachmentItem[] }>(`/api/insurance/${recordId}/attachments`),
    enabled: !!recordId,
  });
}

export function useUploadAttachment(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      uploadFile<{ attachment: AttachmentItem }>(`/api/insurance/${recordId}/attachments`, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "attachments"] });
    },
  });
}

export function useUpdateAttachment(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId, ...body }: { attachmentId: string; sortOrder?: number }) =>
      patchFile<{ attachment: AttachmentItem }>(`/api/insurance/${recordId}/attachments/${attachmentId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "attachments"] });
    },
  });
}

export function useDeleteAttachment(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/api/insurance/${recordId}/attachments/${attachmentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "attachments"] });
    },
  });
}
